import React, { useState, useMemo, useEffect } from "react";
import { Eye, HelpCircle, ArrowLeft } from "lucide-react";
import InstructionForSinglePart from "../../InstructionForSinglePart";

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl shadow-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl font-bold"
        >
          ✕
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const CVForm = ({
  cvStructure = [],
  scenarioData,
  onComplete,  // ✅ Callback to WritingSection
  onBackToFormat,
  viewMode = false
}) => {
  const sections = Array.isArray(cvStructure) ? cvStructure : [];
  // ⭐ ADD THIS FIX — exercise phải được lấy từ scenarioData
  const exercise = scenarioData?.exercise || {};


  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [showTips, setShowTips] = useState(false);
  const [showJobAds, setShowJobAds] = useState(false);
  const [showJobSummary, setShowJobSummary] = useState(false);
  const [showEndCVInstruction, setShowEndCVInstruction] = useState(false);

  const cvFormat = exercise?.format || "chronological";

  // Load Tips
  const tips = useMemo(() => {
    try {
      return exercise.tips ? JSON.parse(exercise.tips) : [];
    } catch {
      return [];
    }
  }, [exercise]);

  // Load Job Ads
  const jobAds = useMemo(() => {
    if (!exercise?.job_ads) return [];
    try {
      return JSON.parse(exercise.job_ads.replace(/\r?\n/g, '').trim());
    } catch {
      return [];
    }
  }, [exercise]);

  // Load Job Summary
  const jobSummaryData = useMemo(() => {
    if (!exercise?.job_summary) return [];
    try {
      return JSON.parse(exercise.job_summary.replace(/\r?\n/g, '').trim());
    } catch {
      return [];
    }
  }, [exercise]);

  const jobKeys = useMemo(() => {
    if (!jobSummaryData.length) return [];
    return Object.keys(jobSummaryData[0]).filter(k => k !== "criteria");
  }, [jobSummaryData]);

  const currentSection = sections[currentSectionIndex];

  // Load Saved CV Data
  useEffect(() => {
    if (scenarioData?.cv_data && typeof scenarioData.cv_data === "object") {
      setFormData(scenarioData.cv_data);
    }
  }, [scenarioData]);

  const handleInputChange = (label, value) => {
    setFormData(prev => ({ ...prev, [label]: value }));
  };

  // Navigation
  const goNext = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(i => i + 1);
    } else {
      if (onComplete) {
        onComplete(formData);  // Gọi ngay để WritingSection mở EndCVSelfCheckModal
      }
    }
  };

  const goPrev = () => {
    if (currentSectionIndex > 0) setCurrentSectionIndex(i => i - 1);
  };

  // ✅ Handle EndCV Instruction Complete
  const handleCompleteChecklist = () => {
    console.log("📋 EndCV Instruction completed");
    setShowEndCVInstruction(false);

    // ✅ Call parent callback with CV data
    if (onComplete) {
      onComplete(formData);
    }
  };

  // Helper functions (same as before)
  const getFieldValueByKeyword = (keywords = []) => {
    const lower = keywords.map(k => k.toLowerCase());
    const field = sections
      .flatMap(s => s.fields || [])
      .find(f => lower.some(k => f.label?.toLowerCase().includes(k)));
    return field ? formData[field.label] : null;
  };

  const firstName = getFieldValueByKeyword(["first name", "given name"]) || "";
  const lastName = getFieldValueByKeyword(["last name", "surname"]) || "";

  const nameField = sections
    .flatMap(s => s.fields || [])
    .find(f => {
      const lb = f.label.toLowerCase();
      return (
        lb === "name" ||
        lb.includes("full name") ||
        lb.includes("your name") ||
        (lb.includes("name") && !lb.includes("company"))
      );
    })?.label;

  const fallbackName = nameField ? formData[nameField] : "";
  const displayName = `${firstName} ${lastName}`.trim() || fallbackName || "YOUR NAME";

  const calculateResumeScore = () => {
    let completed = 0;
    sections.forEach(section => {
      const fields = section.fields || [];
      const allFilled = fields.every(f => {
        const v = formData[f.label];
        return v && v.trim() !== "";
      });
      if (allFilled) completed++;
    });
    return Math.round((completed / sections.length) * 100);
  };

  const resumeScore = calculateResumeScore();

  const normalizeLabel = label => label.replace(" *", "").trim();
  sections.forEach(section => {
    section.fields = section.fields.map(f => ({
      ...f,
      label: normalizeLabel(f.label)
    }));
  });

  // ✅ Render EndCV Instruction Modal
  if (showEndCVInstruction) {
    return (
      <Modal title="End of CV" onClose={() => setShowEndCVInstruction(false)}>
        <InstructionForSinglePart
          data={scenarioData}
          instructionType="endcv"
          onCompleteChecklist={handleCompleteChecklist}
        />
      </Modal>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="bg-white border-b">
          <div className="px-6 py-4">

            {/* BACK + TITLE */}
            <div className="flex items-center justify-start gap-4 mb-4">
              <button
                type="button"
                className="flex items-center text-gray-600 hover:text-gray-800 text-sm"
                onClick={() => onBackToFormat && onBackToFormat()}
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Format Selection
              </button>

              <h2 className="text-xl font-semibold text-gray-800 ml-2">
                {cvFormat.charAt(0).toUpperCase() + cvFormat.slice(1)} CV
              </h2>
            </div>

          </div>
        </div>

        {/* PROGRESS BAR */}
        {sections.length > 0 && (
          <div className="px-6 mt-6">
            <div className="mb-3 flex gap-2">
              {sections.map((_, idx) => (
                <div
                  key={idx}
                  className={`flex-1 h-2 rounded-full transition-all duration-300 ${idx < currentSectionIndex
                      ? "bg-green-500"
                      : idx === currentSectionIndex
                        ? "bg-blue-600"
                        : "bg-gray-300"
                    }`}
                />
              ))}
            </div>

            <div className="flex justify-between text-xs text-gray-600 mb-6">
              {sections.map((sec, idx) => (
                <span
                  key={idx}
                  className={`${idx === currentSectionIndex ? "font-bold text-blue-700" : ""}`}
                >
                  {sec.sectionTitle}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ACTION BUTTONS BELOW PROGRESS */}
        <div className="px-6 mb-4 flex gap-3">
          <button
            onClick={() => setShowTips(true)}
            className="flex items-center px-3 py-1.5 text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50"
          >
            <HelpCircle className="w-4 h-4 mr-1" /> Tips
          </button>

          <button
            onClick={() => setShowJobSummary(true)}
            className="flex items-center px-3 py-1.5 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50"
          >
            <Eye className="w-4 h-4 mr-1" /> Job Summary
          </button>

          <button
            onClick={() => setShowJobAds(true)}
            className="flex items-center px-3 py-1.5 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50"
          >
            <Eye className="w-4 h-4 mr-1" /> Job Ads
          </button>
        </div>


        {/* BODY */}
        <div className="flex gap-6 p-6">
          {/* FORM */}
          <div className="flex-1 bg-white rounded-lg shadow-sm border p-6">
            {currentSection ? (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {currentSection.sectionTitle}
                </h3>

                <div className="space-y-6">
                  {currentSection.fields?.map((field, idx) => (
                    <div key={idx}>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {field.label}{" "}
                        {field.required && <span className="text-red-500">*</span>}
                      </label>

                      {field.type === "textarea" ? (
                        <textarea
                          rows={4}
                          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 text-sm"
                          value={formData[field.label] || ""}
                          onChange={e => handleInputChange(field.label, e.target.value)}
                          disabled={viewMode}
                        />
                      ) : (
                        <input
                          type={field.type || "text"}
                          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 text-sm"
                          value={formData[field.label] || ""}
                          onChange={e => handleInputChange(field.label, e.target.value)}
                          disabled={viewMode}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-between mt-8 pt-6 border-t">
                  <button
                    onClick={goPrev}
                    disabled={currentSectionIndex === 0 || viewMode}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>

                  <button
                    onClick={goNext}
                    disabled={viewMode}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {currentSectionIndex === sections.length - 1
                      ? "Finish CV"
                      : "Next: " + (sections[currentSectionIndex + 1]?.sectionTitle || "Continue")}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-gray-500 italic">No CV structure found.</p>
            )}
          </div>

          {/* PREVIEW */}
          <div className="w-[400px] flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border sticky top-6">
              <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Your resume score</span>
                  <div className="flex items-center">
                    <span
                      className={`text-2xl font-bold ${resumeScore > 70
                        ? "text-green-600"
                        : resumeScore > 40
                          ? "text-orange-600"
                          : "text-red-600"
                        }`}
                    >
                      {resumeScore}%
                    </span>
                    <span className="ml-1 text-xl">
                      {resumeScore > 70 ? "🔥" : resumeScore > 40 ? "⚡" : "💡"}
                    </span>
                  </div>
                </div>

                <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${resumeScore > 70
                      ? "bg-green-500"
                      : resumeScore > 40
                        ? "bg-orange-500"
                        : "bg-red-500"
                      }`}
                    style={{ width: `${resumeScore}%` }}
                  />
                </div>
              </div>

              <div className="p-6 max-h-[calc(100vh-250px)] overflow-y-auto">
                <h3 className="text-2xl font-bold text-center text-gray-900 mb-1">
                  {displayName?.trim() ? displayName.toUpperCase() : "YOUR NAME"}
                </h3>

                <div className="border-t-2 border-gray-300 my-4"></div>

                {sections.map((section, sIdx) => {
                  const sectionContent = section.fields
                    .filter(f => {
                      const fieldValue = formData[f.label]?.trim() || "";
                      if (f.label === nameField) return false;
                      if (
                        fieldValue &&
                        displayName &&
                        displayName.toLowerCase().includes(fieldValue.toLowerCase())
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .map(f => formData[f.label])
                    .filter(v => v && v.trim() !== "");

                  if (sectionContent.length === 0) return null;

                  return (
                    <div key={sIdx} className="mb-6">
                      <h4 className="text-sm font-bold text-gray-900 uppercase mb-2">
                        {section.sectionTitle}
                      </h4>
                      {sectionContent.map((content, i) => (
                        <p key={i} className="text-xs whitespace-pre-line text-gray-800">
                          {content}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showTips && (
        <Modal title="Tips" onClose={() => setShowTips(false)}>
          <div className="space-y-4">
            {tips.length > 0 ? (
              tips.map((tip, idx) => (
                <div key={idx} className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">{tip}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 italic">No tips available</p>
            )}
          </div>
        </Modal>
      )}

      {showJobAds && (
        <Modal title="Job Advertisements" onClose={() => setShowJobAds(false)}>
          <div className="grid md:grid-cols-2 gap-4">
            {jobAds.map((job, idx) => (
              <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-bold text-lg mb-2">{job.title}</h4>
                <div className="text-sm space-y-1 text-gray-700">
                  <p><strong>Company:</strong> {job.company}</p>
                  <p><strong>Location:</strong> {job.location}</p>
                  <p><strong>Type:</strong> {job.type}</p>
                  <p><strong>Requirements:</strong> {job.requirements}</p>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {showJobSummary && (
        <Modal title="Job Summary" onClose={() => setShowJobSummary(false)}>
          <table className="w-full border-collapse border">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">Criteria</th>
                {jobKeys.map(k => (
                  <th key={k} className="border p-2 text-left">{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobSummaryData.map((row, idx) => (
                <tr key={idx}>
                  <td className="border p-2 font-medium">{row.criteria}</td>
                  {jobKeys.map(k => (
                    <td key={k} className="border p-2">{row[k]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
};

export default CVForm;